import {Grid, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {IoInformationCircleSharp} from 'react-icons/io5';
import {useNavigate, useParams} from 'react-router-dom';
import FirestoreManager from '../../api/firebaseManager';
import {Modal} from '../../components/modal/modal';
import {useAddepar} from '../../hooks/use-adepar';
import {useAuth} from '../../hooks/use-auth';
import {useBill} from '../../hooks/use-bill';
import {useBridge} from '../../hooks/use-bridge';
import {usePlaid} from '../../hooks/use-plaid';
import '../../styles/plaid.css';

const UserAccounts = ({user}) => {
  const {id, type} = useParams();
  const navigate = useNavigate();
  const {
    state: {users},
    deleteUser,
  } = useAuth();
  const {
    state: {all_accounts, user_accounts, user_institutions, all_institutions},
    getUserAccounts,
    getUserInstitutions,
    removeUserInstitutions,
  } = usePlaid();
  const {
    state: {user_connections, connections},
    getUserConnections,
    removeUserConnections,
  } = useBill();
  const {
    state: {
      user_connections: addepar_user_connections,
      connections: addepar_connections,
    },
    getUserConnections: getAddeparConnections,
    removeUserConnections: removeAddeparConnections,
  } = useAddepar();
  const {
    state: {
      user_connections: bridge_user_connections,
      connections: bridge_connections,
    },
    getUserConnections: getUserBridgeConnections,
    removeUserConnections: removeUserBridgeConnections,
    editConnection: editBridgeConnection,
  } = useBridge();

  const [info, setInfo] = useState(false);
  const [bridgeReconnecting, setBridgeReconnecting] = useState(false);
  const [bridgeReconnectError, setBridgeReconnectError] = useState('');

  const handleReconnectBridgeAccounts = async () => {
    if (!bridge_cons?.length) {
      setBridgeReconnectError('No Bridge connections found for this user');
      return;
    }

    setBridgeReconnecting(true);
    setBridgeReconnectError('');

    for (const connectionId of bridge_cons) {
      const connection = bridge_connections?.[connectionId] ?? {};
      const {account_mappings, custodian_mappings} = connection;

      if (!custodian_mappings?.length) {
        console.warn(
          `[Reconnect] Connection ${connectionId}: No custodian mappings, skipping`,
        );
        continue;
      }

      const advisor_codes = custodian_mappings.map(
        m => m.advisor_code || m.code,
      );
      const custodian =
        custodian_mappings[0].custodian_code || custodian_mappings[0].source;

      try {
        const response = await FirestoreManager.getBridgeAccounts({
          advisor_codes,
          custodian,
        });

        const existingById = {};
        if (account_mappings) {
          for (const acct of account_mappings) {
            existingById[acct.id] = acct;
          }
        }

        if (response?.success && Array.isArray(response.data)) {
          for (const account of response.data) {
            if (existingById[account.id]) continue;
            existingById[account.id] = {
              id: account.id,
              display_name: account.display_name,
              display_number: account.display_number,
              acct_type: account.acct_type,
              custodian: account.custodian,
              advisor_code: account.advisor_code,
              entity: connection.entity,
              ignore: false,
            };
          }
        }

        const mergedAccounts = Object.values(existingById);
        await editBridgeConnection({
          ...connection,
          account_mappings: mergedAccounts,
        });
      } catch (err) {
        console.error('[Reconnect] Error for connection', connectionId, err);
        setBridgeReconnectError(
          `Error reconnecting: ${err?.message || 'Unknown error'}`,
        );
      }
    }

    setBridgeReconnecting(false);
    getUserBridgeConnections(id);
  };

  const profile = users?.[id] ?? {};
  const {fullName, customerID, emailAddress} = profile;

  const accounts = user_accounts?.[id] ?? [];
  const cons = user_connections?.[id] ?? [];
  const addepar_cons = addepar_user_connections?.[id] ?? [];
  const bridge_cons = bridge_user_connections?.[id] ?? [];

  useEffect(() => {
    if (id && user_accounts?.[id] === undefined) {
      getUserAccounts(id);
    }
    if (id && user_institutions?.[id] === undefined) {
      getUserInstitutions(id);
    }
    if (id && user_connections[id] === undefined) {
      getUserConnections(id);
    }
    if (id && addepar_user_connections?.[id] === undefined) {
      getAddeparConnections(id);
    }
    if (id && bridge_user_connections?.[id] === undefined) {
      getUserBridgeConnections(id);
    }
  }, []);

  return (
    <div className='page-container'>
      <div className='flex-row justify-between align-center'>
        <p style={{fontWeight: 'bold', fontSize: '24px'}}>
          User: {emailAddress} - {type}
        </p>
        <button
          className='gray-button'
          onClick={() => {
            navigate(-1);
          }}
        >
          Done
        </button>
      </div>
      <div className='flex-row justify-between'>
        <table>
          {type === 'plaid' && (
            <thead>
              <tr>
                <th>Forest Entity Name</th>
                <th>Bank Name</th>
                <th>Account Name</th>
                <th>Account Type</th>
                <th>Account Number</th>
              </tr>
            </thead>
          )}
          {type === 'bill' && (
            <thead>
              <tr>
                <th>Forest Entity Name</th>
                <th>BILL Organization Name</th>
                <th>AR Journal</th>
                <th>AP Journal</th>
                <th>VC Journal</th>
                <th>CM Journal</th>
              </tr>
            </thead>
          )}
          {type === 'addepar' && (
            <thead>
              <tr>
                <th>Forest Entity Name</th>
                <th>Addepar Entity Name</th>
                <th>Addepar Entity Id</th>
                <th>Mappings</th>
                <th>Action</th>
              </tr>
            </thead>
          )}
          {type === 'bridgeft' && (
            <thead>
              <tr>
                <th>Forest Entity Name</th>
                <th>Bank Name</th>
                <th>Account Name</th>
                <th>Account Type</th>
                <th>Account Number</th>
              </tr>
            </thead>
          )}
          <tbody>
            {type === 'plaid' &&
              accounts.map(id => {
                const {name, subtype, mask, entity, access_token} =
                  all_accounts[id];
                const inst = all_institutions?.[access_token] ?? {};
                const {name: inst_name} = inst;

                return (
                  <tr key={id}>
                    <td>{entity}</td>
                    <td>{inst_name}</td>
                    <td>{name}</td>
                    <td>{subtype}</td>
                    <td>{mask ? `#${mask}` : '—'}</td>
                  </tr>
                );
              })}
            {type === 'bill' &&
              cons.map(id => {
                const connection = connections?.[id] ?? {};
                const {
                  entity_name,
                  organizations,
                  accounts_payable,
                  accounts_receivable,
                  vendor_credits,
                  credit_memo,
                } = connection;

                return organizations?.map(org => {
                  const {orgName} = org;
                  return (
                    <tr key={`${id}-${orgName}`}>
                      <td>{entity_name || '—'}</td>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {orgName}
                          <IoInformationCircleSharp
                            onClick={() => {
                              setInfo(connection);
                            }}
                            style={{marginLeft: '8px', cursor: 'pointer'}}
                            size={'20px'}
                          />
                        </div>
                      </td>
                      <td>{accounts_receivable || '—'}</td>
                      <td>{accounts_payable || '—'}</td>
                      <td>{vendor_credits || '—'}</td>
                      <td>{credit_memo || '—'}</td>
                    </tr>
                  );
                });
              })}
            {type === 'addepar' &&
              addepar_cons?.map(id => {
                const connection = addepar_connections?.[id] ?? {};
                const entity_mappings = connection.entity_mappings ?? [];
                const account_mappings = connection.account_mappings ?? [];

                // Entity rows to show. Prefer entity_mappings; if a connection
                // has none but still has account_mappings (stranded mappings),
                // derive the rows from the mappings' portfolio_id so they stay
                // reachable. Keeps this page consistent with the dashboard
                // count (which sums account_mappings) and surfaces every
                // connection.
                let rows = entity_mappings.map(m => ({
                  entity_id: m.entity_id,
                  entity_name: m.entity_name,
                }));
                if (!rows.length && account_mappings.length) {
                  const derived = new Map();
                  account_mappings.forEach(am => {
                    const eid = am.portfolio_id ?? am.entity_id;
                    if (eid != null && !derived.has(String(eid))) {
                      derived.set(String(eid), {
                        entity_id: String(eid),
                        entity_name: am.entity_name ?? '(no entity name)',
                      });
                    }
                  });
                  rows = Array.from(derived.values());
                }

                // Always render the connection, even with no mappings at all.
                if (!rows.length) {
                  return (
                    <tr key={`${id}-empty`}>
                      <td colSpan={4} style={{color: '#888', fontStyle: 'italic'}}>
                        Connection {id.slice(0, 8)}… — no mappings
                      </td>
                      <td />
                    </tr>
                  );
                }

                return rows.map((row, idx) => {
                  const {entity_id, entity_name} = row;
                  const count = account_mappings.filter(
                    am =>
                      String(am.portfolio_id ?? am.entity_id) ===
                      String(entity_id),
                  ).length;
                  return (
                    <tr key={`${id}-${entity_id}-${idx}`}>
                      <td>{entity_name}</td>
                      <td>{entity_name}</td>
                      <td>{entity_id}</td>
                      <td>{count}</td>
                      <td>
                        <button
                          style={{color: 'blue'}}
                          onClick={() => {
                            navigate(`/addepar/${id}/mappings/${entity_id}`);
                          }}
                        >
                          View Mappings
                        </button>
                      </td>
                    </tr>
                  );
                });
              })}
            {type === 'bridgeft' &&
              bridge_cons.map(id => {
                const connection = bridge_connections?.[id] ?? {};
                const {account_mappings, custodian_mappings} = connection;
                const bankName = custodian_mappings?.[0]?.custodian || '—';

                return account_mappings?.map(mapping => {
                  const {
                    custodian,
                    entity,
                    acct_type,
                    display_name,
                    display_number,
                  } = mapping;
                  return (
                    <tr key={`${id}-${mapping.id}`}>
                      <td>{entity}</td>
                      <td>{custodian || bankName}</td>
                      <td>{display_name}</td>
                      <td>{acct_type}</td>
                      <td>{display_number ? `#${display_number}` : '—'}</td>
                    </tr>
                  );
                });
              })}
          </tbody>
        </table>
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          {type === 'bridgeft' && (
            <>
              <button
                className='gray-button'
                disabled={bridgeReconnecting}
                onClick={handleReconnectBridgeAccounts}
              >
                {bridgeReconnecting ? 'Reconnecting...' : 'Reconnect Accounts'}
              </button>
              {bridgeReconnectError && (
                <span style={{color: 'red', fontSize: '14px'}}>
                  {bridgeReconnectError}
                </span>
              )}
            </>
          )}
          <button
            className='gray-button'
            onClick={async () => {
              if (
                window.confirm(
                  'Are you sure you want to delete this user? This disconnects all accounts related to the user',
                )
              ) {
                // Proceed with delete operation of accounts and connections
                console.log('Item deleted');

                // DELETE PLAID OBJECTS
                const access_tokens = user_institutions[id];
                await removeUserInstitutions(id, access_tokens);
                // DELETE BILL OBJECTS
                const connection_ids = user_connections[id];
                await removeUserConnections(id, connection_ids);
                // DELETE ADDEPAR OBJECTS
                const addepar_connection_ids = addepar_user_connections[id];
                await removeAddeparConnections(id, addepar_connection_ids);
                // DELETE BRIDGEFT OBJECTS
                const bridgeft_connection_ids =
                  bridge_user_connections[id] || [];
                const bridgeft_connections_to_remove =
                  bridgeft_connection_ids.map(
                    con_id => bridge_connections[con_id],
                  );
                await removeUserBridgeConnections(
                  id,
                  bridgeft_connections_to_remove,
                );
                navigate(-1);
              } else {
                // Cancel delete operation
                console.log('Delete cancelled');
              }
            }}
          >
            Disconnect All Accounts
          </button>
        </div>
      </div>
      <br />
      <br />
      <BillInfoModal info={info} setInfo={setInfo} />
    </div>
  );
};

export const BillInfoModal = ({info, setInfo}) => {
  if (!info) return null;

  const {
    credit_memo,
    company,
    vendor_credits,
    error,
    userName,
    accounts_payable,
    accounts_receivable,
    organizations,
  } = info;

  return (
    <Modal active={!!info} setActive={setInfo}>
      <div className='box flex-column' style={{padding: '20px'}}>
        <Typography variant='h5' sx={{mb: 3, fontWeight: 'bold'}}>
          {userName}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Display field='Company' value={company} />
            <Display field='Credit Memo' value={credit_memo} />
            <Display field='Vendor Credits' value={vendor_credits} />
          </Grid>
          <Grid item xs={6}>
            <Display field='Error' value={error || 'None'} />
            <Display field='Accounts Payable' value={accounts_payable} />
            <Display field='Accounts Receivable' value={accounts_receivable} />
          </Grid>
        </Grid>

        <Typography variant='h6'>Organizations</Typography>
        <ul style={{listStyle: 'none', padding: 0}}>
          {organizations?.map((org, index) => (
            <li key={index}>
              <Typography sx={{marginLeft: '8px'}}>{org.orgName}</Typography>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

const Display = ({field, value}) => {
  return (
    <div style={{marginBottom: '12px'}}>
      <Typography variant='subtitle2' color='text.secondary'>
        {field}
      </Typography>
      <Typography variant='body1'>{value}</Typography>
    </div>
  );
};

export default UserAccounts;
